import { Mail, Linkedin, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { useLanguage } from "../../i18n/LanguageContext";
import { pathFor } from "../../i18n/seoMeta";

const CONTACT_EMAIL = "pan.martin.kuchar@gmail.com";
const LINKEDIN_URL = "https://www.linkedin.com/in/martinkuch/";
const CONTACT_PHONE_DISPLAY = "+420 602 461 799";
const CONTACT_PHONE_TEL = "+420602461799";

export const Footer = () => {
  const { lang, t } = useLanguage();
  const { pathname } = useLocation();
  const homePath = pathFor("home", lang);
  const servicesPath = pathFor("services", lang);
  const isHomePage = pathname === homePath;

  // Footer anchor links use real <Link to="/#anchor"> so they're crawlable
  // and work from non-home routes. On the home page we keep the smooth scroll
  // behavior via the onClick handler.
  const goToAnchor = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      id="contact-details"
      data-testid="site-footer"
      className="bg-[#181B24] text-white pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 pb-12 border-b border-white/10">
          <div className="md:col-span-5">
            <Link to={homePath} aria-label="IT Impact home">
              <Logo />
            </Link>
            <p className="mt-6 max-w-md text-white/70 font-body text-base leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading uppercase tracking-[0.18em] text-sm text-white/60 mb-5">
              {t.footer.navigate}
            </h4>
            <ul className="space-y-3 font-body text-base">
              <li>
                <Link
                  to={homePath}
                  data-testid="footer-link-home"
                  className="text-white/80 hover:text-[#FFE21A] transition-colors"
                >
                  {t.footer.home}
                </Link>
              </li>
              <li>
                <Link
                  to={servicesPath}
                  data-testid="footer-link-services"
                  className="text-white/80 hover:text-[#FFE21A] transition-colors"
                >
                  {t.footer.services}
                </Link>
              </li>
              <li>
                <Link
                  to={`${homePath}#references`}
                  onClick={(e) => {
                    if (isHomePage) {
                      e.preventDefault();
                      goToAnchor("references");
                    }
                  }}
                  data-testid="footer-link-references"
                  className="text-white/80 hover:text-[#FFE21A] transition-colors"
                >
                  {t.footer.references}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-heading uppercase tracking-[0.18em] text-sm text-white/60 mb-5">
              {t.footer.getInTouch}
            </h4>
            <div className="flex flex-col gap-4">
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                data-testid="footer-phone"
                className="inline-flex items-center gap-3 text-white/85 hover:text-[#FFE21A] transition-colors font-body"
              >
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Phone size={16} />
                </span>
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="footer-email"
                className="inline-flex items-center gap-3 text-white/85 hover:text-[#FFE21A] transition-colors font-body"
              >
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail size={16} />
                </span>
                {CONTACT_EMAIL}
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer external"
                referrerPolicy="no-referrer"
                data-testid="footer-linkedin"
                className="inline-flex items-center gap-3 text-white/85 hover:text-[#FFE21A] transition-colors font-body"
              >
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Linkedin size={16} />
                </span>
                LinkedIn
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-white/50 font-body text-sm">
            © {new Date().getFullYear()} IT IMPACT. {t.footer.rights}
          </p>
          <p className="text-white/40 font-body text-xs uppercase tracking-[0.18em]">
            {t.footer.crafted}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
